import utils from "./utils.js";

// Lightbox: open/close, navigation, zoom, autoplay, fullscreen, pagination.
// Uses this.constructor._lbEl to reference the shared static lightbox DOM element
// without creating a circular import with the main class file.

const lightboxMethods = {
    _lbOpen(index) {
        const lb = this.constructor._lbEl;
        if (!lb) return;

        // Store the element that triggered the lightbox so we can restore focus on close
        this._lbTriggerEl = document.activeElement;

        // Stop any existing autoplay
        this._lbStopAutoplay();

        lb._inst = this;
        this.lbOpen = true;

        // Build items list for this lightbox session
        this._lbOriginalIndex = index;
        this._lbItems = this.opts.lightboxSingleImage ? [this.items[index]] : this.items;

        // Apply per-instance style tokens to the shared lightbox DOM
        lb.style.setProperty(
            "--wcpg-lb-img-radius",
            this.opts.lightboxImageBorderRadius + "px",
        );
        lb.style.setProperty(
            "--wcpg-lb-thumb-radius",
            this.opts.lightboxThumbBorderRadius + "px",
        );
        lb.style.setProperty(
            "--wcpg-lb-overlay-bg",
            this.opts.lightboxOverlayColor,
        );

        // Reset per-open state
        this._lbScale = 1;
        lb._lbPanX = 0;
        lb._lbPanY = 0;
        this._lbRotation = 0;
        this._lbFlipH = false;
        this._lbFlipV = false;

        // Kill any active mobile inner zoom so it can't leak into the lightbox
        if (this._zs.mobileZooming && this.mainImg) {
            this._zs.mobileZooming = false;
            this.mainImg.style.transition = "";
            this.mainImg.style.transformOrigin = "";
            this.mainImg.style.transform = "";
            const curItem = this.items[this.index];
            if (curItem && curItem.src) this.mainImg.src = curItem.src;
        }
        if (this._zs.momentumRaf) {
            cancelAnimationFrame(this._zs.momentumRaf);
            this._zs.momentumRaf = null;
        }
        this._suppressClick = false;

        // Capture source element and its viewport rect BEFORE wcpg-lb-active is
        // added (switching display:none→flex reflows the document, which can shift
        // fixed/sticky ancestors and invalidate the measurement).
        // _lbSourceOverride lets callers (e.g. sticky-split scroll items) supply
        // a specific element instead of the default mainImg.
        this._lbSourceEl = this._lbSourceOverride || this.mainImg || null;
        this._lbSourceOverride = null; // consume immediately
        const _lbOpenSrcRect = this._lbSourceEl
            ? this._lbSourceEl.getBoundingClientRect()
            : { width: 0, height: 0, left: 0, top: 0 };

        lb.classList.remove("wcpg-lb-closing");
        lb.classList.add("wcpg-lb-active");
        document.body.classList.add("wcpg-lb-body-open");

        if (this.opts.lightboxZoomOrigin) {
            // Navigate to the target index instantly so the zoom-from-source
            // animation starts with the correct image already in place.
            // Forcing _lbIdx = index makes direction = 0 (no slide), and
            // temporarily setting transition to "none" removes the exit-phase
            // delay so the image begins loading in the same tick.
            this._lbIdx = index;
            const _savedTr = this.opts.lightboxTransition;
            this.opts.lightboxTransition = "none";
            this._lbGoTo(index);
            this.opts.lightboxTransition = _savedTr;
            // _lbGoTo loads the new image via setTimeout(0), so the stale lbImg.src
            // from the previous session is still set at this point. Clear it now so
            // the old image is never visible during the zoom-open animation.
            const _lbImgEl = lb.querySelector(".wcpg-lb-img");
            if (_lbImgEl) _lbImgEl.src = "";
        } else {
            this._lbGoTo(index);
        }
        this._lbSyncToolbar();

        if (!lb._evBound) {
            lb._evBound = true;

            const ov = lb.querySelector(".wcpg-lb-overlay");
            const close = lb.querySelector(".wcpg-lb-close");
            const prev = lb.querySelector(".wcpg-lb-prev");
            const next = lb.querySelector(".wcpg-lb-next");
            const wrap = lb.querySelector(".wcpg-lb-media-wrap");
            const zoomIn = lb.querySelector(".wcpg-lb-zoom-in");
            const zoomOut = lb.querySelector(".wcpg-lb-zoom-out");
            const zoomReset = lb.querySelector(".wcpg-lb-zoom-reset");
            const autoplayBtn = lb.querySelector(".wcpg-lb-autoplay");
            const fsBtn = lb.querySelector(".wcpg-lb-fullscreen");
            const lbLinkedVideoBtnEl = lb.querySelector(".wcpg-lb-linked-video-btn");
            const shareBtn = lb.querySelector(".wcpg-lb-share");
            const shareDropdown = lb.querySelector(".wcpg-lb-share-dropdown");
            const fbBtn = lb.querySelector(".wcpg-lb-share-fb");
            const twBtn = lb.querySelector(".wcpg-lb-share-tw");
            const piBtn = lb.querySelector(".wcpg-lb-share-pi");
            const dlBtn = lb.querySelector(".wcpg-lb-share-dl");

            // Image transformation tools
            const rotateCcwBtn = lb.querySelector(".wcpg-lb-rotate-ccw");
            const rotateCwBtn = lb.querySelector(".wcpg-lb-rotate-cw");
            const flipHBtn = lb.querySelector(".wcpg-lb-flip-h");
            const flipVBtn = lb.querySelector(".wcpg-lb-flip-v");
            const zoom1to1Btn = lb.querySelector(".wcpg-lb-zoom-1to1");
            if (rotateCcwBtn)
                utils.on(rotateCcwBtn, "click", () => lb._inst._lbRotateImage(-90));
            if (rotateCwBtn)
                utils.on(rotateCwBtn, "click", () => lb._inst._lbRotateImage(90));
            if (flipHBtn)
                utils.on(flipHBtn, "click", () => lb._inst._lbFlipImage("h"));
            if (flipVBtn)
                utils.on(flipVBtn, "click", () => lb._inst._lbFlipImage("v"));
            if (zoom1to1Btn)
                utils.on(zoom1to1Btn, "click", () => lb._inst._lbToggle1to1());

            // Share button toggle
            if (shareBtn) {
                utils.on(shareBtn, "click", (e) => {
                    e.stopPropagation();
                    if (!shareDropdown) return;
                    const isOpen = shareDropdown.style.display !== "none";
                    shareDropdown.style.display = isOpen ? "none" : "";
                });
            }
            // Resolve the current media URL + Pinterest image at share click time.
            // Converts iframe embed URLs (youtube.com/embed/ID) to shareable watch URLs.
            const _getShareMedia = () => {
                const _sinst = lb._inst;
                const _sitems = _sinst?._lbItems || _sinst?.items;
                const _sitem = _sitems?.[_sinst?._lbIdx];
                const _svid = lb.querySelector(".wcpg-lb-video");
                const _siframe = lb.querySelector(".wcpg-lb-iframe");
                const _simg = lb.querySelector(".wcpg-lb-img");
                const _isVid = _svid && _svid.style.display !== "none";
                const _isEmbed = _siframe && _siframe.style.display !== "none";
                const _toWatch = (u) => {
                    if (!u) return "";
                    const c = u.split("?")[0];
                    const yt = c.match(/youtube\.com\/embed\/([^/]+)/);
                    if (yt) return "https://www.youtube.com/watch?v=" + yt[1];
                    const vm = c.match(/player\.vimeo\.com\/video\/([^/]+)/);
                    if (vm) return "https://vimeo.com/" + vm[1];
                    return c;
                };
                let url, pinMedia;
                if (_isEmbed) {
                    // Linked embed (image item with linkedVideoEmbed) or direct embed item
                    const _raw = _sitem?.type === "image" ? _sitem?.linkedVideoEmbed : _sitem?.src;
                    url = _toWatch(_raw) || window.location.href;
                    pinMedia = "";
                } else if (_isVid) {
                    url = _svid.src || window.location.href;
                    pinMedia = _sitem?.poster || "";
                } else {
                    url = _simg?.src || window.location.href;
                    pinMedia = _simg?.src || "";
                }
                return { url, pinMedia, caption: _sitem?.caption || document.title || "" };
            };
            if (fbBtn) utils.on(fbBtn, "click", (e) => {
                e.stopPropagation();
                if (shareDropdown) shareDropdown.style.display = "none";
                const { url } = _getShareMedia();
                window.open(
                    "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url),
                    "_blank",
                );
            });
            if (twBtn) utils.on(twBtn, "click", (e) => {
                e.stopPropagation();
                if (shareDropdown) shareDropdown.style.display = "none";
                const { url, caption } = _getShareMedia();
                window.open(
                    "https://x.com/intent/tweet?url=" +
                        encodeURIComponent(url) +
                        "&text=" +
                        encodeURIComponent(caption),
                    "_blank",
                );
            });
            if (piBtn) utils.on(piBtn, "click", (e) => {
                e.stopPropagation();
                if (shareDropdown) shareDropdown.style.display = "none";
                const { url, pinMedia, caption } = _getShareMedia();
                window.open(
                    "https://pinterest.com/pin/create/button/?url=" +
                        encodeURIComponent(url) +
                        (pinMedia ? "&media=" + encodeURIComponent(pinMedia) : "") +
                        "&description=" +
                        encodeURIComponent(caption),
                    "_blank",
                );
            });
            if (dlBtn) utils.on(dlBtn, "click", (e) => {
                e.stopPropagation();
                if (shareDropdown) shareDropdown.style.display = "none";
                const _dlVid = lb.querySelector(".wcpg-lb-video");
                const _dlIsVid = _dlVid && _dlVid.style.display !== "none" && _dlVid.src;
                const _dlHref = _dlIsVid
                    ? _dlVid.src
                    : (lb.querySelector(".wcpg-lb-img") || {}).src;
                if (!_dlHref) return;
                const _dlA = document.createElement("a");
                _dlA.href = _dlHref;
                _dlA.download = _dlHref.split("/").pop().split("?")[0] || (_dlIsVid ? "video" : "image");
                _dlA.target = "_blank";
                _dlA.rel = "noopener noreferrer";
                document.body.appendChild(_dlA);
                _dlA.click();
                document.body.removeChild(_dlA);
            });

            // Close share dropdown when clicking outside
            document.addEventListener("click", lb._evShareClose = () => {
                const sd = lb.querySelector(".wcpg-lb-share-dropdown");
                if (sd) sd.style.display = "none";
            });

            utils.on(close, "click", () => lb._inst._lbClose());

            if (lbLinkedVideoBtnEl) {
                utils.on(lbLinkedVideoBtnEl, "click", (e) => {
                    e.stopPropagation();
                    lb._inst._lbPlayLinkedVideo();
                });
            }

            // Overlay click — only if the click was NOT on media/controls
            utils.on(ov, "click", () => {
                if (lb._inst?.opts?.lightboxCloseOnOverlay)
                    lb._inst._lbClose();
            });

            utils.on(prev, "click", () => {
                lb._inst._lbStopAutoplay();
                lb._inst._lbGoTo(lb._inst._lbIdx - 1);
            });
            utils.on(next, "click", () => {
                lb._inst._lbStopAutoplay();
                lb._inst._lbGoTo(lb._inst._lbIdx + 1);
            });

            // Zoom in / out on lightbox image
            utils.on(zoomIn, "click", () => lb._inst._lbZoom(0.25));
            utils.on(zoomOut, "click", () => lb._inst._lbZoom(-0.25));
            utils.on(zoomReset, "click", () => lb._inst._lbZoomReset());

            // Autoplay toggle
            utils.on(autoplayBtn, "click", () =>
                lb._inst._lbToggleAutoplay(),
            );

            // Fullscreen toggle
            utils.on(fsBtn, "click", () => lb._inst._lbToggleFullscreen());

            // Keyboard
            document.addEventListener(
                "keydown",
                (lb._evKey = (e) => {
                    if (!lb._inst?.lbOpen) return;
                    const k = e.key;

                    // Tab focus trap: keep keyboard focus inside the lightbox
                    if (k === "Tab") {
                        const focusable = Array.from(
                            lb.querySelectorAll(
                                'button:not([disabled]), [tabindex="0"]',
                            ),
                        ).filter(
                            (el) =>
                                el.offsetParent !== null &&
                                getComputedStyle(el).display !== "none",
                        );
                        if (!focusable.length) return;
                        const idx = focusable.indexOf(
                            document.activeElement,
                        );
                        e.preventDefault();
                        if (e.shiftKey) {
                            const prev =
                                idx <= 0
                                    ? focusable[focusable.length - 1]
                                    : focusable[idx - 1];
                            prev.focus();
                        } else {
                            const next =
                                idx >= focusable.length - 1
                                    ? focusable[0]
                                    : focusable[idx + 1];
                            next.focus();
                        }
                        return;
                    }

                    if (k === "Escape") {
                        e.preventDefault();
                        lb._inst._lbClose();
                    }
                    if (k === "ArrowRight") {
                        e.preventDefault();
                        lb._inst._lbStopAutoplay();
                        lb._inst._lbGoTo(
                            lb._inst._lbIdx + (lb._inst._isRTL ? -1 : 1),
                        );
                    }
                    if (k === "ArrowLeft") {
                        e.preventDefault();
                        lb._inst._lbStopAutoplay();
                        lb._inst._lbGoTo(
                            lb._inst._lbIdx + (lb._inst._isRTL ? 1 : -1),
                        );
                    }
                    if (k === " ") {
                        e.preventDefault();
                        lb._inst._lbToggleAutoplay();
                    }
                    // Zoom keys only apply to images — skip for video/embed
                    const _kbVid = lb.querySelector(".wcpg-lb-video");
                    const _kbIframe = lb.querySelector(".wcpg-lb-iframe");
                    const _kbIsMedia =
                        (_kbVid && _kbVid.style.display !== "none") ||
                        (_kbIframe && _kbIframe.style.display !== "none");
                    if (!_kbIsMedia && lb._inst?.opts?.lightboxZoomIn !== false) {
                        if (k === "+" || k === "=") lb._inst._lbZoom(0.25);
                        if (k === "-") lb._inst._lbZoom(-0.25);
                        if (k === "0") lb._inst._lbZoomReset();
                    }
                    if (k === "f" || k === "F")
                        lb._inst._lbToggleFullscreen();
                }),
            );

            // Mouse wheel zoom in media area (images only — skip for video/embed)
            utils.on(
                wrap,
                "wheel",
                (e) => {
                    const _wVid = lb.querySelector(".wcpg-lb-video");
                    const _wIframe = lb.querySelector(".wcpg-lb-iframe");
                    if (
                        (_wVid && _wVid.style.display !== "none") ||
                        (_wIframe && _wIframe.style.display !== "none")
                    )
                        return;
                    if (!lb._inst?.opts?.lightboxScrollZoom) return;
                    e.preventDefault();
                    lb._inst._lbZoom(e.deltaY < 0 ? 0.15 : -0.15);
                },
                { passive: false },
            );

            // Mouse drag: pan when zoomed, or navigate when not zoomed (lightboxSwipe)
            let isDragging = false,
                dragX = 0,
                dragY = 0,
                isDragNav = false,
                navStartX = 0;
            utils.on(wrap, "mousedown", (e) => {
                if (lb._inst._lbScale <= 1) {
                    if (lb._inst?.opts?.lightboxSwipe) {
                        isDragNav = true;
                        navStartX = e.clientX;
                        wrap.style.cursor = "grabbing";
                    }
                    return;
                }
                isDragging = true;
                dragX = e.clientX - lb._lbPanX;
                dragY = e.clientY - lb._lbPanY;
                wrap.style.cursor = "grabbing";
            });
            utils.on(document, "mousemove", (e) => {
                if (!isDragging) return;
                lb._lbPanX = e.clientX - dragX;
                lb._lbPanY = e.clientY - dragY;
                lb._inst._lbApplyImageTransform();
            });
            utils.on(document, "mouseup", (e) => {
                if (isDragging) {
                    isDragging = false;
                    wrap.style.cursor = lb._inst._lbScale > 1 ? "grab"
                        : lb._inst?.opts?.lightboxSwipe ? "grab" : "";
                }
                if (isDragNav) {
                    isDragNav = false;
                    wrap.style.cursor = lb._inst?.opts?.lightboxSwipe ? "grab" : "";
                    const dx = e.clientX - navStartX;
                    if (Math.abs(dx) >= (lb._inst?.opts?.swipeThreshold || 50)) {
                        lb._inst._lbStopAutoplay();
                        const _navNext = lb._inst._isRTL ? dx > 0 : dx < 0;
                        lb._inst._lbGoTo(lb._inst._lbIdx + (_navNext ? 1 : -1));
                    }
                }
            });

            // Touch: pinch-to-zoom + single-finger pan (when zoomed) + swipe to navigate
            let tSx = 0,
                tSy = 0;
            let tDist0 = 0,
                tScale0 = 1;
            let tIsPinch = false;
            let tPanX0 = 0,
                tPanY0 = 0; // pan offset at touch start
            let tSwipeDir = null; // 'h' | 'v' | null — direction lock for swipe nav

            utils.on(
                wrap,
                "touchstart",
                (e) => {
                    if (e.touches.length === 2) {
                        tIsPinch = true;
                        tSwipeDir = null;
                        tDist0 = Math.hypot(
                            e.touches[0].clientX - e.touches[1].clientX,
                            e.touches[0].clientY - e.touches[1].clientY,
                        );
                        tScale0 = lb._inst._lbScale || 1;
                        tPanX0 = lb._lbPanX || 0;
                        tPanY0 = lb._lbPanY || 0;
                    } else {
                        tIsPinch = false;
                        tSwipeDir = null;
                        tSx = e.touches[0].clientX;
                        tSy = e.touches[0].clientY;
                        tPanX0 = lb._lbPanX || 0;
                        tPanY0 = lb._lbPanY || 0;
                    }
                },
                { passive: true },
            );

            utils.on(
                wrap,
                "touchmove",
                (e) => {
                    if (tIsPinch && e.touches.length === 2) {
                        // Pinch-to-zoom — apply immediately (no CSS transition) for
                        // responsive feel; toolbar is synced on touchend.
                        e.preventDefault();
                        const d = Math.hypot(
                            e.touches[0].clientX - e.touches[1].clientX,
                            e.touches[0].clientY - e.touches[1].clientY,
                        );
                        const sc = utils.clamp(
                            tScale0 * (d / tDist0),
                            0.75,
                            5,
                        );
                        lb._inst._lbScale = sc;
                        const img = lb.querySelector(".wcpg-lb-img");
                        const _tRot = lb._inst._lbRotation || 0;
                        const _tScX = sc * (lb._inst._lbFlipH ? -1 : 1);
                        const _tScY = sc * (lb._inst._lbFlipV ? -1 : 1);
                        img.style.transition = "none";
                        img.style.transform = `scale(${_tScX}, ${_tScY}) rotate(${_tRot}deg) translate(${
                            (lb._lbPanX || 0) / sc
                        }px, ${(lb._lbPanY || 0) / sc}px)`;
                    } else if (
                        !tIsPinch &&
                        e.touches.length === 1 &&
                        (lb._inst._lbScale || 1) > 1.05
                    ) {
                        // Single-finger pan while zoomed in
                        e.preventDefault();
                        lb._lbPanX = tPanX0 + (e.touches[0].clientX - tSx);
                        lb._lbPanY = tPanY0 + (e.touches[0].clientY - tSy);
                        const sc = lb._inst._lbScale || 1;
                        const img = lb.querySelector(".wcpg-lb-img");
                        const _pRot = lb._inst._lbRotation || 0;
                        const _pScX = sc * (lb._inst._lbFlipH ? -1 : 1);
                        const _pScY = sc * (lb._inst._lbFlipV ? -1 : 1);
                        img.style.transition = "none";
                        img.style.transform = `scale(${_pScX}, ${_pScY}) rotate(${_pRot}deg) translate(${
                            lb._lbPanX / sc
                        }px, ${lb._lbPanY / sc}px)`;
                    } else if (!tIsPinch && e.touches.length === 1) {
                        // Not zoomed — lock swipe direction on first significant move.
                        // preventDefault() on horizontal gestures stops vertical scroll
                        // so touchend fires with reliable coordinates.
                        if (!tSwipeDir) {
                            const _tdx = Math.abs(e.touches[0].clientX - tSx);
                            const _tdy = Math.abs(e.touches[0].clientY - tSy);
                            if (_tdx > 5 || _tdy > 5)
                                tSwipeDir = _tdx >= _tdy ? "h" : "v";
                        }
                        if (tSwipeDir === "h") e.preventDefault();
                    }
                },
                { passive: false },
            );

            utils.on(wrap, "touchend", (e) => {
                if (tIsPinch) {
                    tIsPinch = false;
                    // Snap back to 1× if barely zoomed; otherwise apply smooth
                    // settle transition and sync the toolbar (reset button etc.)
                    if ((lb._inst._lbScale || 1) < 1.1) {
                        lb._inst._lbZoomReset();
                    } else {
                        lb._inst._lbApplyImageTransform();
                        lb._inst._lbSyncToolbar();
                    }
                    return;
                }
                tSwipeDir = null;
                // Only swipe-navigate when not zoomed in and lightboxSwipe is on
                if ((lb._inst._lbScale || 1) > 1.05) return;
                if (!lb._inst?.opts?.lightboxSwipe) return;
                const dx = e.changedTouches[0].clientX - tSx;
                const dy = e.changedTouches[0].clientY - tSy;
                if (
                    Math.abs(dx) > (lb._inst?.opts?.swipeThreshold || 50) &&
                    Math.abs(dx) > Math.abs(dy)
                ) {
                    lb._inst._lbStopAutoplay();
                    const swipeNext = lb._inst._isRTL ? dx > 0 : dx < 0;
                    lb._inst._lbGoTo(
                        lb._inst._lbIdx + (swipeNext ? 1 : -1),
                    );
                }
            });

            // Fullscreen change (e.g. user presses Escape in browser fullscreen)
            document.addEventListener(
                "fullscreenchange",
                (lb._evFs = () => {
                    const inFs = !!document.fullscreenElement;
                    lb.classList.toggle("wcpg-lb-fullscreen-active", inFs);
                    const fsEnter = lb.querySelector(".wcpg-fs-enter");
                    const fsExit = lb.querySelector(".wcpg-fs-exit");
                    if (fsEnter) fsEnter.style.display = inFs ? "none" : "";
                    if (fsExit) fsExit.style.display = inFs ? "" : "none";
                }),
            );
        }

        if (this.opts.onLightboxOpen)
            this.opts.onLightboxOpen.call(this, index);

        const dur = this.opts.lightboxAnimationDuration;
        const container = lb.querySelector(".wcpg-lb-container");
        const overlay = lb.querySelector(".wcpg-lb-overlay");

        // Overlay instantly opaque — covers the page before scroll lock is applied
        overlay.style.transition = "none";
        overlay.style.opacity = "1";

        // Apply body scroll lock NOW while the overlay is opaque (no visible jump)
        this._lbScrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = -this._lbScrollY + "px";
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";

        const mWrap = lb.querySelector(".wcpg-lb-media-wrap");
        const _wrapRect = mWrap.getBoundingClientRect();
        const _hasSrc = _lbOpenSrcRect.width > 0 && _lbOpenSrcRect.height > 0;

        if (this.opts.lightboxZoomOrigin && _hasSrc) {
            // Zoom-from-source (FLIP open): snap the media wrap to the source image's
            // viewport rect then spring it to its natural grid position.
            // Overlay, toolbar and nav arrows animate in via their existing CSS
            // transitions (triggered by wcpg-lb-visible below) — only the image area zooms.
            const _openScale = Math.max(
                _lbOpenSrcRect.width / _wrapRect.width,
                0.04,
            );
            const _openDx =
                (_lbOpenSrcRect.left + _lbOpenSrcRect.width / 2) -
                (_wrapRect.left + _wrapRect.width / 2);
            const _openDy =
                (_lbOpenSrcRect.top + _lbOpenSrcRect.height / 2) -
                (_wrapRect.top + _wrapRect.height / 2);

            mWrap.style.transition = "none";
            mWrap.style.transformOrigin = "center center";
            mWrap.style.transform = `translate(${_openDx}px, ${_openDy}px) scale(${_openScale})`;

            requestAnimationFrame(() => {
                lb.classList.add("wcpg-lb-visible");
                overlay.style.transition = "";
                overlay.style.opacity = "";

                requestAnimationFrame(() => {
                    const _ease = "cubic-bezier(0.22, 1, 0.36, 1)";
                    mWrap.style.transition = `transform ${dur}ms ${_ease}`;
                    mWrap.style.transform = "translate(0px, 0px) scale(1)";

                    setTimeout(() => {
                        mWrap.style.transition = "";
                        mWrap.style.transform = "";
                        mWrap.style.transformOrigin = "";
                    }, dur + 20);
                });
            });
        } else {
            // Simple fade-in on the container (default)
            container.style.transition = "none";
            container.style.opacity = "0";

            requestAnimationFrame(() => {
                lb.classList.add("wcpg-lb-visible");
                overlay.style.transition = "";
                overlay.style.opacity = "";

                requestAnimationFrame(() => {
                    container.style.transition = `opacity ${dur}ms ease`;
                    container.style.opacity = "1";

                    setTimeout(() => {
                        container.style.transition = "";
                        container.style.opacity = "";
                    }, dur + 20);
                });
            });
        }

        // Move focus into the lightbox after the open animation completes
        setTimeout(() => {
            const closeBtn = lb.querySelector(".wcpg-lb-close");
            if (closeBtn) closeBtn.focus();
        }, dur + 60);

        // Start autoplay if configured
        if (this.opts.lightboxAutoplay) {
            setTimeout(() => this._lbStartAutoplay(), dur + 100);
        }
    },

    _lbClose() {
        const lb = this.constructor._lbEl;
        if (!lb || !this.lbOpen) return;

        // Close share dropdown if open
        const sd = lb.querySelector(".wcpg-lb-share-dropdown");
        if (sd) sd.style.display = "none";

        this._lbStopAutoplay();

        // Exit fullscreen if active
        if (document.fullscreenElement)
            document.exitFullscreen().catch(() => {});

        this.lbOpen = false;
        this._lbResetAllTransforms();

        const dur = this.opts.lightboxAnimationDuration;
        const container = lb.querySelector(".wcpg-lb-container");

        // Release body scroll lock NOW — while the lightbox overlay still covers
        // the page. The overlay is fully opaque at this point so the user never
        // sees the scroll position restore. Doing it inside the setTimeout (after
        // the fade-out) causes a visible blink because the body jumps to y=0 for
        // one paint frame before window.scrollTo can correct it.
        const savedScrollY = this._lbScrollY;
        this._lbScrollY = undefined;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        if (savedScrollY !== undefined) window.scrollTo(0, savedScrollY);

        lb.classList.add("wcpg-lb-closing");
        lb.classList.remove("wcpg-lb-visible");

        {
            const _cmWrap = lb.querySelector(".wcpg-lb-media-wrap");
            const _csEl = this._lbSourceEl;
            const _csr = _csEl ? _csEl.getBoundingClientRect() : null;
            const _hasCsr = _csr && _csr.width > 0;

            if (this.opts.lightboxZoomOrigin && _hasCsr) {
                // Zoom-to-source (FLIP close): animate only the media wrap back to the
                // source image's viewport rect. Overlay, toolbar and arrows fade out
                // simultaneously via the wcpg-lb-closing CSS rules.
                const _wr = _cmWrap.getBoundingClientRect();
                const _cs = Math.max(_csr.width / _wr.width, 0.04);
                const _cdx = (_csr.left + _csr.width / 2) - (_wr.left + _wr.width / 2);
                const _cdy = (_csr.top + _csr.height / 2) - (_wr.top + _wr.height / 2);

                const _cEase = "cubic-bezier(0.55, 0, 1, 0.45)";
                _cmWrap.style.transition = `transform ${dur}ms ${_cEase}`;
                _cmWrap.style.transformOrigin = "center center";
                _cmWrap.style.transform = `translate(${_cdx}px, ${_cdy}px) scale(${_cs})`;
            } else {
                // Simple fade-out on the container (default)
                container.style.transition = `opacity ${dur}ms ease`;
                container.style.opacity = "0";
            }
        }

        const vid = lb.querySelector(".wcpg-lb-video");
        if (vid) {
            vid.pause();
            vid.src = "";
        }
        const lbIframeClose = lb.querySelector(".wcpg-lb-iframe");
        if (lbIframeClose) {
            lbIframeClose.src = "";
        }

        const triggerEl = this._lbTriggerEl;
        this._lbTriggerEl = null;
        setTimeout(() => {
            lb.classList.remove("wcpg-lb-active", "wcpg-lb-closing");
            container.style.transition = "";
            container.style.opacity = "";
            container.style.transform = "";
            container.style.transformOrigin = "";
            const _cwWrap = lb.querySelector(".wcpg-lb-media-wrap");
            if (_cwWrap) {
                _cwWrap.style.transition = "";
                _cwWrap.style.transform = "";
                _cwWrap.style.transformOrigin = "";
            }
            document.body.classList.remove("wcpg-lb-body-open");

            // Always reset mainImg transform — mobile zoom may have left it scaled
            if (this.mainImg) {
                this._zs.mobileZooming = false;
                this.mainImg.style.transition = "";
                this.mainImg.style.transformOrigin = "";
                this.mainImg.style.transform = "";
            }
            this._zs.scale = 1;
            this._zs.panX = 0;
            this._zs.panY = 0;

            // Sync main gallery to the last image viewed in the lightbox.
            // Skip for sticky-split when both sync modes are off — the main
            // viewer is intentionally decoupled from lightbox navigation in that case.
            const _closeSyncIdx = this.opts.lightboxSingleImage
                ? (this._lbOriginalIndex ?? this._lbIdx)
                : this._lbIdx;
            const _ssNosync =
                this.opts.galleryType === "sticky-split" &&
                !this._mobileLayoutActive &&
                !this.opts.stickySplitClickSync;
            if (!_ssNosync && _closeSyncIdx !== undefined && _closeSyncIdx !== this.index)
                this._goTo(_closeSyncIdx);
            this._lbItems = null;
            this._lbOriginalIndex = undefined;

            // Restore focus to the element that originally opened the lightbox
            if (triggerEl && triggerEl.focus) {
                try {
                    triggerEl.focus();
                } catch (_) {}
            }
        }, dur);

        if (this.opts.onLightboxClose) this.opts.onLightboxClose.call(this);
    },

    _lbGoTo(index, direction) {
        // Close share dropdown when navigating
        const _lbEl = this.constructor._lbEl;
        if (_lbEl) {
            const _sd = _lbEl.querySelector(".wcpg-lb-share-dropdown");
            if (_sd) _sd.style.display = "none";
        }

        const inst = this;
        const items = inst._lbItems || inst.items;
        const len = items.length;
        if (!len) return;

        const prevIdx = inst._lbIdx ?? index;
        if (inst.opts.lightboxLoop) index = ((index % len) + len) % len;
        else index = utils.clamp(index, 0, len - 1);

        // Detect wrap-around (last→first or first→last)
        const isWrap =
            (prevIdx === len - 1 && index === 0) ||
            (prevIdx === 0 && index === len - 1);

        if (direction === undefined) {
            if (isWrap) direction = 0;
            else direction = index > prevIdx ? 1 : index < prevIdx ? -1 : 0;
        }

        inst._lbResetAllTransforms();
        inst._lbIdx = index;

        const lb = this.constructor._lbEl;
        const lbImg = lb.querySelector(".wcpg-lb-img");
        const lbVid = lb.querySelector(".wcpg-lb-video");
        const lbIframe = lb.querySelector(".wcpg-lb-iframe");
        const spin = lb.querySelector(".wcpg-lb-spinner");
        const cap = lb.querySelector(".wcpg-lb-caption");
        const ctr = lb.querySelector(".wcpg-lb-counter");
        const prev = lb.querySelector(".wcpg-lb-prev");
        const next = lb.querySelector(".wcpg-lb-next");
        const inner = lb.querySelector(".wcpg-lb-media-inner");
        const mWrap = lb.querySelector(".wcpg-lb-media-wrap");

        // Cancel any in-flight animation
        this._lbAnimKey = (this._lbAnimKey || 0) + 1;
        const animKey = this._lbAnimKey;

        const type = inst.opts.lightboxTransition || "slide";
        const slideW = mWrap ? mWrap.offsetWidth : window.innerWidth;
        const dur = Math.round(inst.opts.lightboxAnimationDuration * 0.75);
        const exitDur = type === "none" ? 0 : Math.round(dur * 0.42);
        const ease = "cubic-bezier(0.42, 0, 0.18, 1)";

        // Wrap: slide uses a short fade+shift; other types treat it like normal nav
        const isWrapSlide = isWrap && type === "slide";

        // In RTL, invert the slide direction so new content enters from the correct side.
        const rtlDir = inst._isRTL ? -direction : direction;
        const rtlWrapDir = (d) => (inst._isRTL ? -d : d);

        const item = items[index];
        const isVid = item.type === "video";
        const isEmbed = item.type === "youtube" || item.type === "vimeo";

        // Stop any currently playing media
        lbVid.pause();
        lbVid.src = "";
        if (lbIframe) lbIframe.src = "";
        const lbLinkedVideoBtn = lb.querySelector(".wcpg-lb-linked-video-btn");
        if (lbLinkedVideoBtn) lbLinkedVideoBtn.style.display = "none";

        // ── Phase 1: exit ─────────────────────────────────────────────────
        if (type === "slide") {
            if (!isWrapSlide) {
                inner.style.transition = `transform ${exitDur}ms ${ease}`;
                inner.style.transform = `translateX(${-rtlDir * slideW}px)`;
            } else {
                const wrapDir = rtlWrapDir(prevIdx === len - 1 ? 1 : -1);
                const wrapX = Math.round(slideW * 0.12);
                inner.style.transition = `transform ${exitDur}ms ease, opacity ${exitDur}ms ease`;
                inner.style.transform = `translateX(${-wrapDir * wrapX}px)`;
                inner.style.opacity = "0";
            }
        } else if (type === "fade") {
            inner.style.transition = `opacity ${exitDur}ms ease`;
            inner.style.opacity = "0";
        } else if (type === "zoom") {
            inner.style.transition = `opacity ${exitDur}ms ease, transform ${exitDur}ms ease`;
            inner.style.opacity = "0";
            inner.style.transform = "scale(0.88)";
        }
        // "none": no exit animation

        if (type !== "none") spin.style.display = "flex";

        // ── Phase 2: load new media, then enter ───────────────────────────
        setTimeout(() => {
            if (inst._lbAnimKey !== animKey) return;

            // Snap inner to entry start position (instant, no transition)
            inner.style.transition = "none";
            if (type === "slide") {
                if (!isWrapSlide) {
                    inner.style.transform = `translateX(${
                        rtlDir * slideW
                    }px)`;
                    inner.style.opacity = "1";
                } else {
                    const wrapDir = rtlWrapDir(
                        prevIdx === len - 1 ? 1 : -1,
                    );
                    const wrapX = Math.round(slideW * 0.12);
                    inner.style.transform = `translateX(${
                        wrapDir * wrapX
                    }px)`;
                    inner.style.opacity = "0";
                }
            } else if (type === "fade") {
                inner.style.transform = "";
                inner.style.opacity = "0";
            } else if (type === "zoom") {
                inner.style.transform = "scale(1.08)";
                inner.style.opacity = "0";
            }

            const enter = () => {
                if (inst._lbAnimKey !== animKey) return;
                spin.style.display = "none";
                // Sync toolbar (zoom/fullscreen visibility) for the newly visible media type
                inst._lbSyncToolbar();

                if (type === "none") return; // instant — already visible

                const enterDur = isWrapSlide ? Math.round(dur * 0.65) : dur;

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (inst._lbAnimKey !== animKey) return;

                        if (type === "slide") {
                            inner.style.transition =
                                `transform ${enterDur}ms ${ease}` +
                                (isWrapSlide
                                    ? `, opacity ${enterDur}ms ease`
                                    : "");
                            inner.style.transform = "translateX(0)";
                            inner.style.opacity = "1";
                        } else if (type === "fade") {
                            inner.style.transition = `opacity ${enterDur}ms ease`;
                            inner.style.transform = "";
                            inner.style.opacity = "1";
                        } else if (type === "zoom") {
                            inner.style.transition = `opacity ${enterDur}ms ease, transform ${enterDur}ms ease`;
                            inner.style.transform = "scale(1)";
                            inner.style.opacity = "1";
                        }

                        setTimeout(() => {
                            if (inst._lbAnimKey !== animKey) return;
                            inner.style.transition = "";
                            inner.style.transform = "";
                            inner.style.opacity = "";
                        }, enterDur + 20);
                    });
                });
            };

            if (isEmbed) {
                lbImg.style.display = "none";
                lbVid.style.display = "none";
                if (lbIframe) {
                    lbIframe.style.display = "block";
                    lbIframe.src = item.embed || item.src;
                }
                enter();
            } else if (isVid) {
                lbImg.style.display = "none";
                lbVid.style.display = "block";
                if (lbIframe) lbIframe.style.display = "none";
                lbVid.loop = inst.opts.videoHostedLoop;
                lbVid.muted = inst.opts.videoHostedMuted;
                lbVid.src = item.src;
                lbVid.poster = item.poster || "";
                lbVid.load();
                if (inst.opts.videoHostedAutoplay) {
                    lbVid.play().catch(() => {});
                }
                enter();
            } else {
                lbImg.style.display = "";
                lbVid.style.display = "none";
                if (lbIframe) lbIframe.style.display = "none";
                utils.loadImage(item.zoomSrc || item.src).then((loaded) => {
                    if (inst._lbAnimKey !== animKey) return;
                    lbImg.src = loaded.src;
                    lbImg.alt = item.caption || "";
                    const _lvBtn = lb.querySelector(".wcpg-lb-linked-video-btn");
                    if (_lvBtn) _lvBtn.style.display = item.linkedVideoType ? "" : "none";
                    // Auto-play linked video when the relevant autoplay setting is on.
                    if (item.linkedVideoType) {
                        const _lvIsEmbed =
                            item.linkedVideoType === "youtube" ||
                            item.linkedVideoType === "vimeo";
                        if (
                            (_lvIsEmbed && inst.opts.videoAutoplay) ||
                            (!_lvIsEmbed && inst.opts.videoHostedAutoplay)
                        ) {
                            inst._lbPlayLinkedVideo();
                        }
                    }
                    enter();
                });
            }
        }, exitDur);

        // ── Update UI (counter, caption, arrows, pagination) ──────────────
        if (inst.opts.lightboxShowCaption && cap)
            cap.textContent = item.caption || "";
        if (inst.opts.lightboxShowCounter && ctr)
            ctr.textContent = len > 1 ? `${index + 1} / ${len}` : "";

        if (!inst.opts.lightboxLoop) {
            prev.classList.toggle("wcpg-btn-disabled", index === 0);
            next.classList.toggle("wcpg-btn-disabled", index === len - 1);
        } else {
            prev.classList.remove("wcpg-btn-disabled");
            next.classList.remove("wcpg-btn-disabled");
        }
        if (len <= 1 || !inst.opts.lightboxShowArrows) {
            prev.style.display = "none";
            next.style.display = "none";
        } else {
            prev.style.display = "";
            next.style.display = "";
        }

        if (inst.opts.lightboxShowThumbnails)
            inst._buildLbPagination(lb, index);
    },

    _buildLbPagination(lb, activeIdx) {
        const strip = lb.querySelector(".wcpg-lb-thumbs-strip");
        strip.innerHTML = "";
        const items = this._lbItems || this.items;

        if (this.opts.lightboxPaginationType === "dots") {
            strip.className = "wcpg-lb-thumbs-strip wcpg-lb-dots-strip";
            items.forEach((_, i) => {
                const dot = utils.el(
                    "button",
                    "wcpg-lb-dot" +
                        (i === activeIdx ? " wcpg-lb-dot-active" : ""),
                );
                dot.setAttribute("aria-label", `Go to image ${i + 1}`);
                dot.setAttribute("data-index", i);
                utils.on(dot, "click", () => lb._inst._lbGoTo(i));
                strip.appendChild(dot);
            });
        } else {
            strip.className = "wcpg-lb-thumbs-strip";
            items.forEach((item, i) => {
                const t = utils.el(
                    "div",
                    "wcpg-lb-thumb-item" +
                        (i === activeIdx ? " wcpg-lb-thumb-active" : ""),
                );
                t.setAttribute("data-index", i);
                t.setAttribute("tabindex", "0");
                t.setAttribute("role", "button");
                t.setAttribute("aria-label", `View image ${i + 1}`);
                const isLbMedia = item.type !== "image";
                if (!isLbMedia || item.thumb) {
                    const img = utils.el("img");
                    img.src = item.thumb || item.src;
                    img.alt = item.caption || "";
                    img.draggable = false;
                    if (isLbMedia) {
                        img.onerror = () => {
                            img.remove();
                            t.classList.add("wcpg-thumb-no-thumb");
                        };
                    }
                    t.appendChild(img);
                } else {
                    t.classList.add("wcpg-thumb-no-thumb");
                }
                if (isLbMedia) {
                    const b = utils.el("div", "wcpg-thumb-play-badge");
                    b.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>`;
                    t.appendChild(b);
                }
                if (!isLbMedia && item.linkedVideoType) {
                    const b = utils.el("div", "wcpg-thumb-play-badge");
                    b.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>`;
                    t.appendChild(b);
                    t.classList.add("wcpg-thumb-has-video");
                }
                utils.on(t, "click", () => lb._inst._lbGoTo(i));
                utils.on(t, "keydown", (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        lb._inst._lbGoTo(i);
                    }
                });
                strip.appendChild(t);
            });
        }
    },

    // ── Lightbox: image zoom (pan+scale inside lightbox) ────────────────────────
    _lbZoom(delta) {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        if (lb.querySelector(".wcpg-lb-video").style.display !== "none")
            return;
        lb._inst._lbScale = utils.clamp(
            (lb._inst._lbScale || 1) + delta,
            1,
            5,
        );
        lb._inst._lbApplyImageTransform();
        lb._inst._lbSyncToolbar();
    },

    _lbZoomReset() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        lb._inst = lb._inst || this;
        lb._inst._lbScale = 1;
        lb._lbPanX = 0;
        lb._lbPanY = 0;
        lb._inst._lbApplyImageTransform();
        lb._inst._lbSyncToolbar();
    },

    _lbApplyImageTransform() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        const img = lb.querySelector(".wcpg-lb-img");
        const sc = lb._inst._lbScale || 1;
        const px = lb._lbPanX || 0;
        const py = lb._lbPanY || 0;
        const rot = lb._inst._lbRotation || 0;
        const fH = lb._inst._lbFlipH || false;
        const fV = lb._inst._lbFlipV || false;
        const scX = sc * (fH ? -1 : 1);
        const scY = sc * (fV ? -1 : 1);
        img.style.transition = "transform 0.18s ease";
        img.style.transform = `scale(${scX}, ${scY}) rotate(${rot}deg) translate(${px / sc}px, ${py / sc}px)`;
        const wrap = lb.querySelector(".wcpg-lb-media-wrap");
        wrap.style.cursor = (sc > 1 || lb._inst?.opts?.lightboxSwipe) ? "grab" : "";
    },

    _lbSyncToolbar() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        const inst = lb._inst || this;
        const opts = inst.opts;
        const sc = inst._lbScale || 1;
        const reset = lb.querySelector(".wcpg-lb-zoom-reset");
        const zin = lb.querySelector(".wcpg-lb-zoom-in");
        const zout = lb.querySelector(".wcpg-lb-zoom-out");
        const autoplayBtn = lb.querySelector(".wcpg-lb-autoplay");
        const fsBtn = lb.querySelector(".wcpg-lb-fullscreen");

        // Detect current media type from live DOM state
        const lbVid = lb.querySelector(".wcpg-lb-video");
        const lbIframe = lb.querySelector(".wcpg-lb-iframe");
        const isVid = lbVid && lbVid.style.display !== "none";
        const isEmbed = lbIframe && lbIframe.style.display !== "none";
        const isMedia = isVid || isEmbed;

        // Zoom buttons: hidden when lightboxZoomIn is off, or for video/embed
        const showZoom = opts.lightboxZoomIn !== false;
        [zin, zout, reset].forEach((b) => {
            if (!b) return;
            if (!showZoom || isMedia) {
                b.style.display = "none";
                b.style.opacity = "";
            } else {
                b.style.display = "";
                b.style.opacity = "";
            }
        });
        if (showZoom && !isMedia) {
            if (reset) reset.style.display = sc > 1 ? "" : "none";
            if (zout) zout.classList.toggle("wcpg-tool-disabled", sc <= 1);
            if (zin) zin.classList.toggle("wcpg-tool-disabled", sc >= 5);
        }

        // Autoplay button — hidden when single-image mode is on (nothing to cycle through)
        if (autoplayBtn)
            autoplayBtn.style.display =
                opts.lightboxShowAutoplayButton !== false && !opts.lightboxSingleImage ? "" : "none";

        // Fullscreen button
        if (fsBtn)
            fsBtn.style.display = opts.lightboxFullscreen !== false ? "" : "none";

        // Share button
        const shareWrap = lb.querySelector(".wcpg-lb-share-wrap");
        if (shareWrap) {
            shareWrap.style.display = opts.lightboxShare ? "" : "none";
            if (opts.lightboxShare) {
                const _fbBtn = lb.querySelector(".wcpg-lb-share-fb");
                const _twBtn = lb.querySelector(".wcpg-lb-share-tw");
                const _piBtn = lb.querySelector(".wcpg-lb-share-pi");
                const _dlBtn = lb.querySelector(".wcpg-lb-share-dl");
                if (_fbBtn) _fbBtn.style.display = opts.lightboxShareFacebook !== false ? "" : "none";
                if (_twBtn) _twBtn.style.display = opts.lightboxShareTwitter !== false ? "" : "none";
                if (_piBtn) _piBtn.style.display = opts.lightboxSharePinterest !== false ? "" : "none";
                if (_dlBtn) {
                    // Hide for embeds (YouTube/Vimeo); show for images and hosted videos
                    const _dlHide = isEmbed || opts.lightboxShareDownload === false;
                    _dlBtn.style.display = _dlHide ? "none" : "";
                    if (!_dlHide) {
                        const _dlLabel = _dlBtn.querySelector(".wcpg-dl-label");
                        if (_dlLabel) _dlLabel.textContent = isVid ? "Download video" : "Download image";
                    }
                }
            }
        }

        // Image transformation tools (center toolbar)
        const imageTools = lb.querySelector(".wcpg-lb-image-tools");
        if (imageTools) {
            const showImageTools = opts.lightboxImageTools !== false;
            imageTools.style.display = (!showImageTools || isMedia) ? "none" : "";
            if (showImageTools && !isMedia) {
                const fhBtn = lb.querySelector(".wcpg-lb-flip-h");
                const fvBtn = lb.querySelector(".wcpg-lb-flip-v");
                const z1Btn = lb.querySelector(".wcpg-lb-zoom-1to1");
                if (fhBtn) fhBtn.classList.toggle("wcpg-lb-tool-active", !!(inst._lbFlipH));
                if (fvBtn) fvBtn.classList.toggle("wcpg-lb-tool-active", !!(inst._lbFlipV));
                if (z1Btn) z1Btn.classList.toggle("wcpg-lb-tool-active", (inst._lbScale || 1) > 1.05);
            }
        }
    },

    // ── Image transformation actions ─────────────────────────────────────────

    _lbResetAllTransforms() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        lb._inst = lb._inst || this;
        const inst = lb._inst;
        inst._lbScale = 1;
        lb._lbPanX = 0;
        lb._lbPanY = 0;
        inst._lbRotation = 0;
        inst._lbFlipH = false;
        inst._lbFlipV = false;
        const img = lb.querySelector(".wcpg-lb-img");
        if (img) { img.style.transition = ""; img.style.transform = ""; }
        const wrap = lb.querySelector(".wcpg-lb-media-wrap");
        if (wrap) wrap.style.cursor = inst.opts?.lightboxSwipe ? "grab" : "";
        inst._lbSyncToolbar();
    },

    _lbRotateImage(deg) {
        this._lbRotation = ((this._lbRotation || 0) + deg + 360) % 360;
        this._lbApplyImageTransform();
        this._lbSyncToolbar();
    },

    _lbFlipImage(axis) {
        if (axis === "h") this._lbFlipH = !this._lbFlipH;
        else this._lbFlipV = !this._lbFlipV;
        this._lbApplyImageTransform();
        this._lbSyncToolbar();
    },

    _lbToggle1to1() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        const img = lb.querySelector(".wcpg-lb-img");
        const wrap = lb.querySelector(".wcpg-lb-media-wrap");
        if (!img || !img.naturalWidth || !img.naturalHeight || !wrap) return;

        // Calculate the zoom level that makes the image fill the wrap area edge-to-edge
        // (cover mode): the constraining axis at scale=1 determines the fill multiplier.
        const imgAR = img.naturalWidth / img.naturalHeight;
        const wrapAR = wrap.offsetWidth / wrap.offsetHeight;
        const fillScale = utils.clamp(
            imgAR > wrapAR
                ? wrap.offsetHeight / (wrap.offsetWidth / imgAR)  // width-constrained → fill height axis
                : wrap.offsetWidth / (wrap.offsetHeight * imgAR), // height-constrained → fill width axis
            1,
            5,
        );

        // Toggle: if already at fill scale, return to 1×; otherwise zoom to fill
        if (Math.abs((this._lbScale || 1) - fillScale) < 0.1) {
            this._lbScale = 1;
        } else {
            this._lbScale = fillScale;
        }
        lb._lbPanX = 0;
        lb._lbPanY = 0;
        this._lbApplyImageTransform();
        this._lbSyncToolbar();
    },

    // ── Autoplay ─────────────────────────────────────────────────────────────
    _lbStartAutoplay() {
        const lb = this.constructor._lbEl;
        if (!lb || !this.lbOpen) return;
        const delay = this.opts.lightboxAutoplayDelay || 3500;
        this._lbAutoplayTimer = setInterval(() => {
            if (!lb._inst?.lbOpen) {
                this._lbStopAutoplay();
                return;
            }
            lb._inst._lbGoTo(lb._inst._lbIdx + 1, 1);
        }, delay);
        lb.classList.add("wcpg-lb-autoplaying");
        const btn = lb.querySelector(".wcpg-lb-autoplay");
        if (btn) {
            btn.querySelector(".wcpg-play-icon").style.display = "none";
            btn.querySelector(".wcpg-pause-icon").style.display = "";
            btn.setAttribute("title", "Pause slideshow");
        }
    },

    _lbStopAutoplay() {
        clearInterval(this._lbAutoplayTimer);
        this._lbAutoplayTimer = null;
        const lb = this.constructor._lbEl;
        if (!lb) return;
        lb.classList.remove("wcpg-lb-autoplaying");
        const btn = lb.querySelector(".wcpg-lb-autoplay");
        if (btn) {
            btn.querySelector(".wcpg-play-icon").style.display = "";
            btn.querySelector(".wcpg-pause-icon").style.display = "none";
            btn.setAttribute("title", "Start slideshow");
        }
    },

    _lbToggleAutoplay() {
        if (this._lbAutoplayTimer) this._lbStopAutoplay();
        else this._lbStartAutoplay();
    },

    // ── Share ────────────────────────────────────────────────────────────────
    // ── Fullscreen ───────────────────────────────────────────────────────────
    _lbToggleFullscreen() {
        const lb = this.constructor._lbEl;
        if (!lb) return;

        // Exit fullscreen if already active
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
            return;
        }

        // For hosted video: fullscreen the <video> element so the browser
        // sizes it to fill the screen using its native controls.
        // For embeds (YouTube/Vimeo): fullscreen the <iframe> — the iframe
        // already carries allowfullscreen so modern browsers permit this,
        // and the player's own controls remain intact inside the frame.
        // For images: fullscreen the whole lightbox overlay as before.
        const lbVid = lb.querySelector(".wcpg-lb-video");
        const lbIframe = lb.querySelector(".wcpg-lb-iframe");
        const isVid = lbVid && lbVid.style.display !== "none";
        const isEmbed = lbIframe && lbIframe.style.display !== "none";

        if (isVid && lbVid.requestFullscreen) {
            lbVid.requestFullscreen().catch(() => {});
        } else if (isEmbed && lbIframe.requestFullscreen) {
            lbIframe.requestFullscreen().catch(() => {});
        } else {
            lb.requestFullscreen().catch(() => {});
        }
    },
};

export default lightboxMethods;
